import { AlertCircle, RotateCw } from "lucide-react";
import { useId } from "react";
import { useWatch } from "react-hook-form";

import type { PseBank } from "@/modules/dashboard/payins/services/pseBanks.service";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaComboboxField } from "@/shared/components/forms/ColocaComboboxField";
import { ColocaInputField } from "@/shared/components/forms/ColocaInputField";
import { ColocaRadioGroupField } from "@/shared/components/forms/ColocaRadioGroupField";
import { ColocaSelectField } from "@/shared/components/forms/ColocaSelectField";
import useTranslations from "@/shared/lang/useTranslations";

import { PSE_FIELD_MAX, pseDocumentTypesFor, type PsePersonType } from "./psePayer.schema";
import { PsePaymentVoucher } from "./PsePaymentVoucher";

export interface PsePayerFieldsProps {
  /** Short operation reference shown on the voucher. */
  reference: string;
  /** Amount already formatted for display, or `null` until the operation has been priced. */
  amount: string | null;
  currency: string;
  /** `null` while the directory is still loading. */
  banks: PseBank[] | null;
  banksError: string | null;
  banksLoading: boolean;
  onRetryBanks: () => void;
}

/** Section heading — the same mono eyebrow the payment surfaces use for a labelled block. */
function GroupHeading({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <h3 id={id} className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
      {children}
    </h3>
  );
}

/**
 * Everything the PSE network needs about the person authorizing the debit, laid out for one pass.
 *
 * Single column throughout — multi-column forms measurably raise omission and error rates, and the one
 * exception here is deliberate: document type and document number are a single datum, so they share a row.
 * The eight fields are split into two named blocks ("who pays" / "contact and bank") so the step reads as two
 * short asks rather than one wall, without spending an extra click on a third wizard step.
 *
 * Render inside a `<ColocaForm>` built by `usePsePayerForm` — every field name here matches that schema.
 */
export function PsePayerFields({
  reference,
  amount,
  currency,
  banks,
  banksError,
  banksLoading,
  onRetryBanks,
}: PsePayerFieldsProps) {
  const { t } = useTranslations("payins");
  const identityHeadingId = useId();
  const contactHeadingId = useId();

  const personType = useWatch({ name: "personType" }) as PsePersonType;
  const isCompany = personType === "juridica";

  const bankItems = (banks ?? []).map((bank) => ({ value: bank.code, label: bank.name }));

  return (
    <div className="space-y-5">
      <PsePaymentVoucher amount={amount} currency={currency} reference={reference} />

      <section aria-labelledby={identityHeadingId} className="space-y-3">
        <GroupHeading id={identityHeadingId}>{t("pse_group_identity")}</GroupHeading>

        {/* Every field in this form is required — `psePayer.schema.ts` declares no `.optional()` — so the
            asterisk is on all eight. It replaces nothing: validation stays on blur and on submit. */}
        <ColocaRadioGroupField
          name="personType"
          label={t("pse_person_type_label")}
          required
          orientation="horizontal"
          items={[
            { value: "natural", label: t("pse_person_natural") },
            { value: "juridica", label: t("pse_person_juridica") },
          ]}
        />

        {/* Type + number are one datum, which is the only reason they share a row. The select locks to NIT
            for a legal entity — the network admits nothing else.

            `items-start` is what keeps the two aligned. A `FormItem` is a grid whose `align-content`
            resolves to `stretch`, so when this row grows — an error under the number, or just the label
            wrapping on a narrow screen — the left cell stretched and its own grid shared the extra height
            between its label and its control, dropping the select half a line below the input. */}
        <div className="grid grid-cols-[7.5rem_1fr] items-start gap-3">
          <ColocaSelectField
            name="documentType"
            label={t("pse_document_type_label")}
            required
            disabled={isCompany}
            items={pseDocumentTypesFor(personType ?? "natural").map((code) => ({
              value: code,
              label: code,
            }))}
          />
          <ColocaInputField
            name="documentNumber"
            label={t("pse_document_number_label")}
            required
            inputMode="numeric"
            maxLength={PSE_FIELD_MAX.documentNumber}
            autoComplete="off"
            placeholder={t("pse_document_placeholder")}
            transform={(raw) => raw.replace(/[^\d]/g, "")}
          />
        </div>

        <ColocaInputField
          name="payerName"
          label={t("pse_name_label")}
          required
          maxLength={PSE_FIELD_MAX.payerName}
          autoComplete="name"
          placeholder={t("pse_name_placeholder")}
        />
      </section>

      <section aria-labelledby={contactHeadingId} className="space-y-3">
        <GroupHeading id={contactHeadingId}>{t("pse_group_contact")}</GroupHeading>

        <ColocaInputField
          name="email"
          type="email"
          label={t("pse_email_label")}
          required
          maxLength={PSE_FIELD_MAX.email}
          autoComplete="email"
          placeholder={t("pse_email_placeholder")}
        />
        <ColocaInputField
          name="phone"
          label={t("pse_phone_label")}
          required
          inputMode="tel"
          maxLength={10}
          autoComplete="tel-national"
          placeholder={t("pse_phone_placeholder")}
          transform={(raw) => raw.replace(/[^\d]/g, "")}
        />
        <ColocaInputField
          name="address"
          label={t("pse_address_label")}
          required
          maxLength={PSE_FIELD_MAX.address}
          autoComplete="street-address"
          placeholder={t("pse_address_placeholder")}
        />

        <div className="space-y-2">
          <ColocaComboboxField
            name="bankCode"
            label={t("pse_bank_label")}
            required
            placeholder={banksLoading && !banks ? t("pse_bank_loading") : t("pse_bank_placeholder")}
            emptyMessage={t("pse_bank_empty")}
            description={t("pse_bank_hint")}
            disabled={!!banksError}
            items={bankItems}
          />
          {banksError && (
            <div className="flex flex-wrap items-center gap-2 text-xs text-destructive">
              <AlertCircle className="size-3.5 shrink-0" aria-hidden="true" />
              <span>{t("pse_bank_error")}</span>
              <ColocaButton
                type="button"
                variant="ghost"
                size="xs"
                icon={RotateCw}
                loading={banksLoading}
                onClick={onRetryBanks}
              >
                {t("pse_bank_retry")}
              </ColocaButton>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
